// frontend/src/components/Dashboard.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  PlusIcon,
  UsersIcon,
  MapIcon,
  DocumentIcon,
  ArrowPathIcon,
  CreditCardIcon,
  ArrowLeftIcon,
  BriefcaseIcon,
  UserGroupIcon,
  UserPlusIcon,
  CalculatorIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { fetchAnalyticsCounts, fetchWeeklyRevenue } from '../api/dashboardApi';
import axiosInstance from '../utils/axiosInstance';
import Layout from './Layout';
import { useMobileDetector } from '../utils/deviceDetection';

const StripeStatusIndicator = ({ connected }) => (
  <div className="flex items-center space-x-2 ml-4">
    <div className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
    <span className={`text-sm ${connected ? 'text-green-400' : 'text-red-400'}`}>
      {connected ? 'Stripe connected' : 'Stripe not connected'}
    </span>
  </div>
);

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({ jobs: 0, customers: 0, employees: 0 });
  const [weeklyRevenue, setWeeklyRevenue] = useState(null);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [success, setSuccess] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const isMobile = useMobileDetector();

  const quickActions = [
    {
      to: '/jobs',
      icon: BriefcaseIcon,
      text: 'Jobs Hub'
    },
    {
      to: '/customers',
      icon: UserGroupIcon,
      text: 'Customer Hub'
    },
    {
      to: '/quotes?tab=create',
      icon: DocumentIcon,
      text: 'Create Quote'
    },
    {
      to: '/employees?tab=create',
      icon: UserPlusIcon,
      text: 'Add Employee'
    },
    {
      to: '/route-map',
      icon: MapIcon,
      text: 'Route Map'
    },
    {
      to: '/analytics',
      icon: ChartBarIcon,
      text: 'Analytics'
    }
  ];

  const checkStripeStatus = async () => {
    try {
      const response = await axiosInstance.get('/connect/status');
      
      const statusData = {
        isConnected: !!response.data.accountId,
        accountId: response.data.accountId,
        isOnboarded: response.data.isOnboarded
      };
      
      setStripeConnected(statusData.isOnboarded);
      cacheStripeStatus(statusData);
      
      if (response.data.accountId) {
        setStripeAccountId(response.data.accountId);
      }
    } catch (error) {
      console.error('Dashboard: Error checking Stripe status:', error);
      setStripeConnected(false);
    }
  };

  useEffect(() => {
    const initializeDashboard = async () => {
      if (!user) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        await checkStripeStatus();
        await fetchDashboardData();
        setInitialLoadComplete(true);
      } catch (error) {
        console.error('Dashboard: Error during initialization:', error);
        setError('Failed to load dashboard data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, [user]);

  useEffect(() => {
    if (!initialLoadComplete || !user) return;

    const interval = setInterval(() => {
      fetchDashboardData();
      checkStripeStatus();
    }, 600000);

    return () => clearInterval(interval);
  }, [initialLoadComplete, user]);

  useEffect(() => {
    if (stripeAccountId && user && !user.stripeConnectAccountId) {
      console.log('Dashboard: Updating user with Stripe account ID');
      refreshUser().catch(err => {
        console.error('Dashboard: Failed to refresh user data:', err);
      });
    }
  }, [stripeAccountId, user?.stripeConnectAccountId]);

  const getStripeStatusFromCache = () => {
    try {
      const cachedData = localStorage.getItem('stripeStatus');
      if (!cachedData) return null;
      const { status, timestamp } = JSON.parse(cachedData);
      const cacheAge = Date.now() - timestamp;
      if (cacheAge < 1800000) return status; 
      else return null;
    } catch (error) { console.error('Error reading cache:', error); return null; }
  };

  const cacheStripeStatus = (status) => {
    try {
      localStorage.setItem('stripeStatus', JSON.stringify({ status, timestamp: Date.now() }));
    } catch (error) { console.error('Error caching status:', error); }
  };

  const handleConnectStripe = async () => {
    try {
      console.log('Dashboard: Initializing Stripe Connect...');
      setError(null);
      setSuccess(null);
      localStorage.removeItem('stripeStatus');
      const response = await axiosInstance.post('/connect/init');
      
      if (response.data.status === 'already_onboarded') {
        console.log('Dashboard: Stripe already connected:', response.data.message);
        setSuccess(response.data.message);
        checkStripeStatus();
        return;
      }
      
      if (response.data.url) {
        console.log('Dashboard: Redirecting to Stripe Connect onboarding...');
        window.location.href = response.data.url;
      } else {
        setError('Failed to get Stripe Connect URL. Please try again.');
      }
    } catch (error) {
      console.error('Dashboard: Error connecting to Stripe:', error);
      if (error.response?.data?.error?.includes('platform-profile')) {
        setError('Stripe platform setup is incomplete. Please contact support.');
      } else {
        setError('Failed to initialize Stripe Connect: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching dashboard data...');
      const [countsData, revenueData] = await Promise.all([
        fetchAnalyticsCounts(),
        fetchWeeklyRevenue()
      ]);

      console.log('Dashboard data fetched:', { counts: countsData, revenue: revenueData });
      
      // Ensure countsData has default values if any property is missing
      const sanitizedCounts = {
        jobs: countsData?.jobs || 0,
        customers: countsData?.customers || 0,
        employees: countsData?.employees || 0,
        ...countsData // Keep any additional properties
      };
      
      setCounts(sanitizedCounts);
      setWeeklyRevenue(revenueData?.weeklyRevenue || 0);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Stripe connection component adjusted for mobile
  const renderStripeConnection = () => {
    if (stripeConnected) {
      return (
        <div className={`mb-4 ${isMobile ? 'p-3' : 'p-4'} bg-green-800/20 rounded-lg shadow-lg border border-green-700/30`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="flex-shrink-0 bg-white p-1.5 rounded-full">
                <CreditCardIcon className={`${isMobile ? 'h-4 w-4' : 'h-6 w-6'} text-green-600`} />
              </div>
              <div>
                <div className="flex items-center">
                  <h3 className={`${isMobile ? 'text-sm' : 'text-lg'} font-semibold text-white mr-2`}>
                    {isMobile ? 'Stripe' : 'Stripe Payment Processing'}
                  </h3>
                  <div className={`flex items-center space-x-1 px-1.5 py-0.5 bg-green-500/20 rounded-full`}>
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span className="text-xs font-medium text-green-300">Connected</span>
                  </div>
                </div>
                {!isMobile && (
                  <p className="text-gray-300 text-sm">Your Stripe account is connected and ready to accept payments.</p>
                )}
              </div>
            </div>
            <Link
              to="/profile?tab=payments"
              className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} bg-white/10 text-white rounded-md hover:bg-white/20 transition-colors border border-white/20`}
            >
              Manage
            </Link>
          </div>
        </div>
      );
    }

    if (isMobile) {
      return (
        <div className="mb-4 p-3 bg-gradient-to-r from-blue-700/30 to-blue-900/30 rounded-lg shadow-lg border border-blue-600/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="bg-white p-1.5 rounded-full">
                <CreditCardIcon className="h-4 w-4 text-blue-600" />
              </div>
              <span className="text-sm font-semibold text-white">Connect Stripe</span>
            </div>
            <button
              onClick={handleConnectStripe}
              className="px-2 py-1 bg-white text-blue-600 rounded-md text-xs font-medium"
            >
              Connect
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-8 p-5 bg-gradient-to-r from-blue-700/30 to-blue-900/30 rounded-lg shadow-xl border border-blue-600/30 transform hover:scale-[1.01] transition-all duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 bg-white p-2 rounded-full">
              <CreditCardIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="text-xl font-bold text-white">Accept Payments with Stripe</h3>
                <div className="flex items-center space-x-1 px-2 py-0.5 bg-red-500/20 rounded-full border border-red-400/30">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                  <span className="text-xs font-medium text-red-300">Not Connected</span>
                </div>
              </div>
              <p className="text-blue-100 mb-4">Connect your Stripe account to start accepting payments.</p>
              <button
                onClick={handleConnectStripe}
                className="px-4 py-2 bg-white text-blue-600 rounded-md font-medium hover:bg-blue-50 transition-colors inline-flex items-center shadow-lg hover:shadow-white/20"
              >
                <CreditCardIcon className="h-5 w-5 mr-2" />
                Connect Now
              </button>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="text-right">
              <div className="text-white font-bold mb-1">Enable Payment Features</div>
              <div className="text-blue-200 text-sm">Takes ~5 minutes</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Function to render the quick actions section
  const renderQuickActions = () => (
    <div className={`${isMobile ? 'mb-4' : 'mb-6 sm:mb-8'}`}>
      <h2 className={`${isMobile ? 'text-lg mb-2' : 'text-xl sm:text-2xl mb-4 sm:mb-6'} font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400`}>
        Quick Actions
      </h2>
      <div className={`grid ${isMobile ? 'grid-cols-3 gap-2' : 'grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-3 lg:grid-cols-6'}`}>
        {quickActions.map((action, index) => (
          <QuickActionButton
            key={action.text + '-' + index}
            to={action.to}
            icon={action.icon}
            text={action.text}
            isMobile={isMobile}
          />
        ))}
      </div>
    </div>
  );

  // Function to render the overview cards
  const renderOverviewCards = () => (
    <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 gap-3 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3'} mb-6 sm:mb-8`}>
      <OverviewCard 
        title="Active Jobs"
        count={counts?.jobs ?? 0} 
        link="/jobs?tab=manage"
        loading={loading}
        icon={<BriefcaseIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />}
        isMobile={isMobile}
        subscriptionTier={user?.subscriptionTier}
      />
      <OverviewCard 
        title="Active Customers"
        count={counts?.customers ?? 0} 
        link="/customers?tab=manage"
        loading={loading}
        icon={<UserGroupIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />}
        isMobile={isMobile}
        subscriptionTier={user?.subscriptionTier}
      />
      <StatCard
        icon={<CurrencyDollarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-400" />}
        title="Weekly Revenue"
        value={weeklyRevenue !== null ? `$${weeklyRevenue.toFixed(2)}` : 'N/A'}
        link="/analytics"
        color="from-purple-500 to-purple-600"
        loading={loading}
        isMobile={isMobile}
      />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-8 px-3 sm:px-4 md:px-6 lg:px-8">
      {location.pathname !== '/dashboard' && (
        <button
          onClick={handleBack}
          className="mb-4 sm:mb-6 flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          <span>Back</span>
        </button>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6">
        <div className="relative mb-3 sm:mb-0">
          <div className="absolute inset-0 blur-[100px] bg-gradient-to-r from-blue-500/30 to-purple-500/30 transform rotate-12"></div>
          <h1 className={`relative ${isMobile ? 'text-xl' : 'text-2xl sm:text-4xl'} font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400`}>
            Welcome, {user?.name || 'User'}
          </h1>
        </div>
        <div className="flex items-center">
          <button
            onClick={fetchDashboardData}
            className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 sm:px-4 sm:py-2 text-sm'} rounded-md bg-gradient-to-r from-green-500 to-green-600 text-white font-medium hover:from-green-600 hover:to-green-700 transform hover:scale-105 transition duration-200 shadow-lg hover:shadow-green-500/50 flex items-center disabled:opacity-50`}
            disabled={loading}
          >
            <ArrowPathIcon className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2'} ${loading ? 'animate-spin' : ''}`} />
            <span className={isMobile ? 'text-xs' : 'text-sm sm:text-base'}>Refresh</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-100 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-900/30 border border-green-700 text-green-100 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6" role="alert">
          <p>{success}</p>
        </div>
      )}

      {/* Mobile-first layout - Quick Actions at the top */}
      {isMobile && (
        <>
          {renderQuickActions()}
          {renderStripeConnection()}
          {renderOverviewCards()}
        </>
      )}

      {/* Desktop layout - Stats first, then Stripe, then Quick Actions */}
      {!isMobile && (
        <>
          {renderStripeConnection()}
          {renderOverviewCards()}
          {renderQuickActions()}
        </>
      )}
    </div>
  );
};

const OverviewCard = ({ title, count, link, loading, icon, isMobile, subscriptionTier }) => {
  const getLimit = () => {
    switch (subscriptionTier) {
      case 'basic': return 25;
      case 'pro': return 100;
      case 'enterprise': return '∞';
      default: return 25;
    }
  };

  const limit = getLimit();
  const percentage = limit === '∞' ? 0 : Math.min((count / limit) * 100, 100);
  const progressColor = percentage >= 90 ? 'bg-red-500' : percentage >= 70 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className={`bg-gradient-to-br from-gray-800/50 to-gray-800/80 ${isMobile ? 'p-3' : 'p-6'} rounded-lg border border-gray-700 shadow-lg hover:shadow-xl transition duration-300 transform hover:scale-105 flex flex-col justify-between`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className={`${isMobile ? 'text-sm' : 'text-lg'} font-medium text-blue-400`}>{title}</h3>
        {icon && <div className={`${isMobile ? 'p-1' : 'p-2'} rounded-lg bg-gray-700/50`}>{icon}</div>}
      </div>
      <Link to={link} className="group">
        <div className={`${isMobile ? 'text-2xl mb-2' : 'text-3xl mb-4'} font-semibold text-white group-hover:text-blue-400 transition-colors`}>
          {loading ? (
            <div className="animate-pulse bg-gray-700 h-8 w-16 rounded"></div>
          ) : (
            title === 'Active Customers' ? `${count ?? 0}/${limit}` : count ?? 'N/A'
          )}
        </div>
        {title === 'Active Customers' && limit !== '∞' && (
          <div className="mt-2">
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${progressColor}`}
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {percentage.toFixed(0)}% used
            </div>
          </div>
        )}
        <div className="mt-auto">
          <div className="text-sm text-blue-400 group-hover:text-blue-300 transition-colors duration-200 flex items-center">
            View Details <ChevronRightIcon className="h-4 w-4 ml-1" /> 
          </div>
        </div>
      </Link>
    </div>
  );
};

const StatCard = ({ icon, title, value, link, color = "from-blue-500 to-blue-600", loading, isMobile }) => (
  <div className={`bg-gradient-to-br from-gray-800/50 to-gray-800/80 ${isMobile ? 'p-3' : 'p-6'} rounded-lg border border-gray-700 shadow-lg hover:shadow-xl transition duration-300 transform hover:scale-105 flex flex-col justify-between`}>
    <div className="flex items-center justify-between mb-2">
      <div className={`${isMobile ? 'p-1.5' : 'p-3'} rounded-lg bg-gradient-to-br ${color} shadow-md`}>
        {icon}
      </div>
      <Link to={link} className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-400 hover:text-blue-300 transition-colors duration-200`}>
        View Details <ChevronRightIcon className="h-4 w-4 inline" />
      </Link>
    </div>
    <Link to={link} className="group">
      <div>
        <h3 className={`${isMobile ? 'text-sm' : 'text-lg'} font-medium text-blue-400 mb-1 group-hover:text-blue-300 transition-colors`}>{title}</h3>
        <div className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-semibold text-white group-hover:text-blue-400 transition-colors`}>
          {loading ? (
            <div className="animate-pulse bg-gray-700 h-8 w-24 rounded"></div>
          ) : (
            value ?? 'N/A'
          )}
        </div>
      </div>
    </Link>
  </div>
);

const QuickActionButton = ({ to, icon: IconComponent, text, onClick, isMobile }) => {
  const handleClick = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick(e);
    }
  };

  const content = (
     <div className="flex flex-col items-center space-y-1">
       {React.isValidElement(IconComponent) ? IconComponent : <IconComponent className={`${isMobile ? 'h-5 w-5' : 'h-8 w-8'} text-blue-400 group-hover:text-blue-300 transition-colors duration-200`} />}
       <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-white group-hover:text-blue-300 transition-colors duration-200 text-center`}>{text}</span>
     </div>
  );

  const className = `bg-gradient-to-br from-gray-800/50 to-gray-800/80 ${isMobile ? 'p-2' : 'p-4'} rounded-lg text-center border border-gray-700 shadow-lg hover:shadow-xl transition duration-300 transform hover:scale-105 group w-full h-full flex items-center justify-center`;

  if (to) {
    return (
      <Link to={to} className={className} onClick={handleClick}>
         {content}
      </Link>
    );
  }

  return (
    <button onClick={handleClick} className={className}>
      {content}
    </button>
  );
};

export default Dashboard;
